import { ChangeDetectorRef, Component, HostListener, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ReportService } from '../..';
import { ReservationContainerComponent } from '@app/_components/reservation/components/reservation/reservation-container/reservation-container.component';

export interface DrilldownDialogData {
    metric: string;
    title: string;
    startDate: Date;
    endDate: Date;
    locationId: number | null;
    /** When provided, the dialog skips its own fetch and renders these rows
     *  directly. This is the workaround for the Material Dialog 14 overlay
     *  CD bug: opening the dialog with rows already populated avoids the
     *  loading→data transition that overlay repaint refuses to honor. */
    preloadedRows?: any[];
    /** Optional error message — if set, dialog opens directly in error state. */
    preloadedError?: string | null;
}

/**
 * Generic drill-down dialog for the Operations Dashboard.
 *
 * Receives a metric key + the current filter context, fetches rows from
 * /api/2.6/operations-dashboard/drilldown, and renders them in a simple
 * table. Column set varies by metric. Reservation rows are clickable —
 * opens the existing ReservationContainerComponent.
 */
@Component({
    selector: 'app-operations-drilldown-dialog',
    templateUrl: './operations-drilldown-dialog.component.html',
    styleUrls: ['./operations-drilldown-dialog.component.scss'],
    standalone: false
})
export class OperationsDrilldownDialogComponent implements OnInit, OnDestroy {

    /** Plain state — no getter/setter side effects. */
    viewState: 'loading' | 'error' | 'empty' | 'data' = 'loading';
    /** Kept as a plain field for any code that still reads it. */
    loading = true;
    error: string | null = null;
    rows: any[] = [];
    metric: string;
    title: string;


    /**
     * RoomStatusEnum code → display name. Mirrors the constants in
     * com.jmotto.util.RoomStatusEnum on the backend. Used by the 'status'
     * column formatter so the dialog shows "Dirty" instead of "2".
     */
    private readonly STATUS_NAMES: { [code: number]: string } = {
        1: 'Occupied',
        2: 'Dirty',
        3: 'Housekeeping',
        4: 'Ready for Inspection',
        5: 'Inspection',
        6: 'Clean / Ready',
        7: 'Guest Checked-In',
        8: 'In Progress',
        9: 'Not Available',
    };

    /** Track the in-flight subscription so we can cancel it on close / re-fetch. */
    private sub: Subscription | null = null;
    /** Hard timeout — if no response in 15 s, surface an error so the dialog
     *  never gets permanently stuck on "Loading…" (the rare hung-request case). */
    private loadingTimeoutHandle: any = null;
    private static readonly LOADING_TIMEOUT_MS = 15000;

    constructor(
        public dialogRef: MatDialogRef<OperationsDrilldownDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DrilldownDialogData,
        private reportService: ReportService,
        private dialog: MatDialog,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private zone: NgZone) {
        this.metric = data.metric;
        this.title  = data.title;
    }

    ngOnInit(): void {
        // Workaround for the Material Dialog overlay CD bug — if the parent
        // pre-fetched the rows, render them directly without ever entering
        // the loading state. The overlay won't repaint during a viewState
        // transition, so we skip the transition entirely.
        if (this.data.preloadedError) {
            this.error = this.data.preloadedError;
            this.viewState = 'error';
            this.loading = false;
        } else if (Array.isArray(this.data.preloadedRows)) {
            this.rows = this.data.preloadedRows;
            this.viewState = this.rows.length > 0 ? 'data' : 'empty';
            this.loading = false;
        } else {
            this.fetch();
        }
    }

    ngOnDestroy(): void {
        if (this.sub) { this.sub.unsubscribe(); this.sub = null; }
        if (this.loadingTimeoutHandle) { clearTimeout(this.loadingTimeoutHandle); this.loadingTimeoutHandle = null; }
    }

    fetch(): void {
        if (this.sub) { this.sub.unsubscribe(); this.sub = null; }
        if (this.loadingTimeoutHandle) { clearTimeout(this.loadingTimeoutHandle); this.loadingTimeoutHandle = null; }
        this.viewState = 'loading';
        this.loading = true;
        this.error = null;
        this.rows = [];
        // Hard timeout — guarantees the dialog never spins forever even if
        // the HTTP layer or an interceptor swallows the response.
        this.loadingTimeoutHandle = setTimeout(() => {
            this.zone.run(() => {
                if (this.viewState === 'loading') {
                    this.error = 'Drill-down timed out. Try closing and re-opening.';
                    this.viewState = 'error';
                    this.loading = false;
                    try { if (this.sub) { this.sub.unsubscribe(); this.sub = null; } } catch (e) { /* swallow */ }
                    this.cdr.detectChanges();
                }
            });
        }, OperationsDrilldownDialogComponent.LOADING_TIMEOUT_MS);
        this.sub = this.reportService.operationsDashboardDrilldown(this.metric, {
            startDate:  this.data.startDate,
            endDate:    this.data.endDate,
            locationId: this.data.locationId,
        }).subscribe({
            next: (res: any) => {
                this.zone.run(() => {
                    if (this.loadingTimeoutHandle) { clearTimeout(this.loadingTimeoutHandle); this.loadingTimeoutHandle = null; }
                    if (res && res.success) {
                        this.rows = (res.data && res.data.rows) || [];
                        this.viewState = this.rows.length > 0 ? 'data' : 'empty';
                    } else {
                        this.error = (res && res.error) || 'Unknown error loading drill-down';
                        this.viewState = 'error';
                    }
                    this.loading = false;
                    this.cdr.detectChanges();
                    // eslint-disable-next-line no-console
                    console.log('[drilldown] response received: loading=' + this.loading + ' viewState=' + this.viewState + ' rows=' + this.rows.length);
                });
            },
            error: (err) => {
                this.zone.run(() => {
                    if (this.loadingTimeoutHandle) { clearTimeout(this.loadingTimeoutHandle); this.loadingTimeoutHandle = null; }
                    this.error = err?.message || 'Network error loading drill-down';
                    this.viewState = 'error';
                    this.loading = false;
                    this.cdr.detectChanges();
                });
            }
        });
    }

    /** Columns to render for each metric type. Returns array of {field, header, type?}. */
    columns(): { field: string; header: string; type?: string }[] {
        switch (this.metric) {
            case 'arrivals':
            case 'departures':
                return [
                    { field: 'reservationId',     header: 'Res ID' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'guestFirstName',    header: 'First' },
                    { field: 'guestLastName',     header: 'Last' },
                    { field: 'checkInDateTime',   header: 'Check-in',  type: 'date' },
                    { field: 'checkOutDateTime',  header: 'Check-out', type: 'date' },
                    { field: 'numberOfNightsStay',header: 'Nights',    type: 'num'  },
                    { field: 'reservationStatus', header: 'Status' },
                ];
            case 'occupied':
            case 'vacantClean':
            case 'outOfOrder':
            case 'cleaningBacklog':
            case 'allUnits':
            case 'unitsDirty':
            case 'unitsHousekeeping':
            case 'unitsInspection':
                return [
                    { field: 'shortTermRentalId', header: 'STR ID',   type: 'propertyLink' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'propertyName',      header: 'Name' },
                    { field: 'bedrooms',          header: 'Bedrooms', type: 'num' },
                    { field: 'occupancyStatus',   header: 'Status',   type: 'status' },
                ];
            case 'futonsEligible':
            case 'rollawaysEligible':
                return [
                    { field: 'shortTermRentalId', header: 'STR ID',   type: 'propertyLink' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'propertyName',      header: 'Name' },
                    { field: 'bedrooms',          header: 'Bedrooms', type: 'num' },
                    { field: 'capacity',          header: 'Capacity', type: 'num' },
                ];
            case 'futonsRevenue':
            case 'rollawaysRevenue':
                return [
                    { field: 'orderId',           header: 'Order' },
                    { field: 'saleDate',          header: 'Sale Date', type: 'date' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'productName',       header: 'Product' },
                    { field: 'quantity',          header: 'Qty', type: 'num' },
                    { field: 'listPrice',         header: 'List Price', type: 'money' },
                    { field: 'adjustment',        header: 'Adj', type: 'money' },
                    { field: 'lineTotal',         header: 'Line Total', type: 'money' },
                    { field: 'guestFirstName',    header: 'First' },
                    { field: 'guestLastName',     header: 'Last' },
                ];
            case 'guestsOnProperty':
                return [
                    { field: 'reservationId',     header: 'Res ID' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'guestFirstName',    header: 'First' },
                    { field: 'guestLastName',     header: 'Last' },
                    { field: 'bedrooms',          header: 'Bedrooms', type: 'num' },
                    { field: 'checkInDateTime',   header: 'Check-in',  type: 'date' },
                    { field: 'checkOutDateTime',  header: 'Check-out', type: 'date' },
                ];
            case 'futonsOut':
            case 'rollawaysOut':
                return [
                    { field: 'reservationId',     header: 'Res ID' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'productName',       header: 'Product' },
                    { field: 'quantity',          header: 'Qty', type: 'num' },
                    { field: 'guestFirstName',    header: 'First' },
                    { field: 'guestLastName',     header: 'Last' },
                    { field: 'checkInDateTime',   header: 'Check-in',  type: 'date' },
                    { field: 'checkOutDateTime',  header: 'Check-out', type: 'date' },
                ];
            case 'cancellations':
                return [
                    { field: 'reservationId',     header: 'Res ID' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'guestFirstName',    header: 'First' },
                    { field: 'guestLastName',     header: 'Last' },
                    { field: 'cancelDate',        header: 'Cancelled',  type: 'date' },
                    { field: 'checkInDateTime',   header: 'Was Check-in', type: 'date' },
                    { field: 'revenueLost',       header: 'Revenue Lost', type: 'money' },
                ];
            case 'bookingsCreated':
                return [
                    { field: 'orderId',           header: 'Order' },
                    { field: 'saleDate',          header: 'Booked', type: 'date' },
                    { field: 'type',              header: 'Channel' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'guestFirstName',    header: 'First' },
                    { field: 'guestLastName',     header: 'Last' },
                    { field: 'checkInDateTime',   header: 'Check-in', type: 'date' },
                    { field: 'status',            header: 'Status' },
                ];
            case 'sameDayTurnovers':
                return [
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'departingResId',    header: 'Departing Res' },
                    { field: 'departingLastName', header: 'Departing Guest' },
                    { field: 'arrivingResId',     header: 'Arriving Res' },
                    { field: 'arrivingLastName',  header: 'Arriving Guest' },
                ];
            case 'priorityRooms':
                return [
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'reservationId',     header: 'Res ID' },
                    { field: 'occupancyStatus',   header: 'Status', type: 'status' },
                    { field: 'checkInDateTime',   header: 'Check-in', type: 'date' },
                ];
            default:
                return Object.keys(this.rows[0] || {}).map(k => ({ field: k, header: k }));
        }
    }

    format(row: any, col: { field: string; type?: string }): string {
        const v = row[col.field];
        if (v == null) return '—';
        switch (col.type) {
            case 'date':
                return new Date(v).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
            case 'num':
                return Number(v).toLocaleString('en-US');
            case 'money':
                return Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
            case 'status':
                return this.STATUS_NAMES[Number(v)] || String(v);
            case 'propertyLink':
                // Link text is the raw STR id; the template renders it inside
                // an <a> element. Returning a string keeps a sane fallback for
                // any non-link rendering paths.
                return String(v);
            default:
                return String(v);
        }
    }

    /** Reservation-linked rows are clickable. Opens the existing reservation dialog. */
    onRowClick(row: any): void {
        const resId = row.reservationId || row.arrivingResId;
        if (!resId) return;
        const cfg = new MatDialogConfig();
        cfg.disableClose = false;
        cfg.autoFocus = true;
        cfg.hasBackdrop = true;
        cfg.data = { reservationId: resId, reservation: row, showCloseButton: true };
        this.dialog.open(ReservationContainerComponent, cfg);
    }

    /**
     * Build the absolute URL for the property-edit route. Used by the STR ID
     * anchor's [href] binding so the browser handles the link natively —
     * regular click opens in the configured target (_blank = new tab),
     * middle-click / ctrl-click / "open in new tab" from the context menu
     * also work. The dialog stays open in the original tab so the user can
     * jump to several properties from the same drill-down.
     */
    propertyEditUrl(strId: any): string {
        if (strId == null || strId === '') return 'javascript:void(0)';
        const tree = this.router.createUrlTree(['/property-edit', strId]);
        return this.router.serializeUrl(tree);
    }

    /**
     * Defensive close — cancel any in-flight request and force-close the dialog.
     * Wrapped in zone.run so the click is guaranteed to run inside Angular's
     * change-detection zone even if the HTTP layer left us outside it. We
     * also flip `loading=false` before calling dialogRef.close() so the
     * *ngIf-guarded loading block tears down on the same tick — that
     * unblocks Material's animation callback chain.
     */
    close(): void {
        this.zone.run(() => {
            try { if (this.sub) { this.sub.unsubscribe(); this.sub = null; } } catch (e) { /* swallow */ }
            if (this.loadingTimeoutHandle) { clearTimeout(this.loadingTimeoutHandle); this.loadingTimeoutHandle = null; }
            this.loading = false;
            this.viewState = 'empty';
            try { this.cdr.detectChanges(); } catch (e) { /* swallow */ }
            try { this.dialogRef.close(); } catch (e) { /* swallow */ }
        });
    }

    /** ESC always closes the dialog — independent of any in-flight HTTP. */
    @HostListener('document:keydown.escape')
    onEsc(): void {
        this.close();
    }
}
